import { Button, SvgArrowDownward, SvgArrowUpward, Toast } from '@loomhq/lens';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';

import {
  requestVideoPlayerCurrentTime,
  requestVideoPlayerPlay,
  updateEmojiClickedInVideoPlayer,
  updateVideoPlayerPlaying,
} from 'actions/videoPlayer';
import {
  TRANSCRIPTION_BACK_TO_ACTIVE_SCROLLING_CLICK,
  TRANSCRIPTION_CORRECTION_CORRECT_ALL,
  TRANSCRIPTION_CORRECTION_CORRECT_SINGLE_WORD,
  TRANSCRIPTION_CORRECTION_DELETE_WORD,
  TRANSCRIPTION_CORRECTION_WORD_SELECTED,
  TRANSCRIPTION_NAVIGATED_BY_PHRASE_CLICK,
  TRANSCRIPTION_NAVIGATED_BY_USER_SCROLL,
} from 'constants/events';
import { TRANSCRIPT_PANEL_MODE_CORRECTION } from 'reducers/shareVideo';
import {
  TranscriptionState,
  applyLocalCorrection,
  openCorrectionPopup,
  updateTranscriptCorrectionMessage,
} from 'reducers/transcription';
import { selectUserIsOwner } from 'selectors/shareVideo';
import {
  selectCurrentSearchIndex,
  selectEditedRowIndex,
  selectIsTranscriptCorrectionsPopUpOpen,
  selectIsTranscriptSearchOpen,
  selectTranscriptCorrectionMessage,
  selectTranscriptCorrectionsEnabled,
  selectTranscriptPanelMode,
  selectTranscriptSearchPhrase,
  selectTranscriptSearchPhraseIndexArray,
  selectTranscriptSelectedOccurrenceIndex,
  selectTranscriptTextToCorrect,
} from 'selectors/transcripts';
import {
  selectCurrentVideoId,
  selectEmojiClickedInVideoPlayer,
  selectHasStarted,
  selectPaused,
  selectPlayerTime,
} from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import {
  findIndexOfOccurrence,
  findTranscriptSourceIndexes,
} from 'utilities/transcript/findSearchWordInTranscript';
import { Transcript } from 'utilities/transcript/types';
import { findPhraseIndex } from 'utilities/transcription';

import EmptyTranscript from '../emptyTranscript';
import TranscriptRow from '../transcript-row';

import TranscriptCorrectionPopup from 'components/share-video/transcript/transcript-correction-popup';

import styles from './styles.module.less';

const TOP = 'top';
const BOTTOM = 'bottom';
const UP = 'up';
const DOWN = 'down';

const TEXT_NODE = 3;

const wordRegExp = /\w+/;

const TranscriptList = ({
  transcript,
  setShowFullHeader,
}: {
  transcript: Transcript;
  setShowFullHeader: (v: boolean) => void;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { phrases } = transcript;
  const listRef = useRef<{ scrollToItem; resetAfterIndex }>();
  const rowHeights = useRef({});
  const currentPlayTime = useSelector<unknown, number>(selectPlayerTime);
  const hasStarted = useSelector<unknown, boolean>(selectHasStarted);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [buttonLocation, setButtonLocation] = useState(TOP);
  const [arrowDirection, setArrowDirection] = useState(UP);
  const [firstHookCall, setFirstHookCall] = useState(false);
  const [atEnd, setAtEnd] = useState(false);
  const paused = useSelector(selectPaused);
  const emojiClicked = useSelector(selectEmojiClickedInVideoPlayer);
  const [userScrolled, setUserScrolled] = useState(false);
  const [firstVisibleIndex, setFirstVisibleIndex] = useState(0);
  const isTranscriptSearchOpen = useSelector(selectIsTranscriptSearchOpen);
  const searchPhrase = useSelector(selectTranscriptSearchPhrase);
  const indexArray = useSelector(selectTranscriptSearchPhraseIndexArray);
  const searchIndex = useSelector<unknown, number>(selectCurrentSearchIndex);
  // @ts-expect-error ignore due to enabling strict null checks
  const csIndex = indexArray[searchIndex - 1]?.index;
  // @ts-expect-error ignore due to enabling strict null checks
  const counter = indexArray[searchIndex - 1]?.counter;
  const currentUserIsOwner = useSelector(selectUserIsOwner);
  const videoId = useSelector<unknown, string>(selectCurrentVideoId);
  const panelMode = useSelector(selectTranscriptPanelMode);
  const popUpIsOpen = useSelector(selectIsTranscriptCorrectionsPopUpOpen);
  const textToBeCorrected = useSelector<unknown, string>(
    selectTranscriptTextToCorrect
  );
  const editedRowIndex = useSelector<unknown, number>(selectEditedRowIndex);
  const selectedOccurrenceIndex = useSelector<unknown, number>(
    selectTranscriptSelectedOccurrenceIndex
  );
  const correctionMessage = useSelector<unknown, string>(
    selectTranscriptCorrectionMessage
  );
  const correctionsEnabled = useSelector<TranscriptionState, boolean>(
    selectTranscriptCorrectionsEnabled
  );

  const showCorrectionToastMessage =
    Boolean(correctionMessage) &&
    panelMode === TRANSCRIPT_PANEL_MODE_CORRECTION;

  const getRowHeight = (index?: number) => {
    if (index === undefined) {
      return 60;
    }

    return rowHeights.current[index] || 60;
  };

  const setRowHeight = (index, size) => {
    rowHeights.current[index] = size;
    listRef?.current?.resetAfterIndex(index ? index - 1 : index);
  };

  useEffect(() => {
    if (!phrases || phrases.length < 1) {
      return;
    }

    // update state to ensure that listRef.current has been set
    // to have listref.current scrollToItem if transcript is open
    // when video is paused
    if (!firstHookCall) {
      setFirstHookCall(true);
    }

    const calculatedIndex = findPhraseIndex(currentPlayTime, phrases);

    if (emojiClicked && listRef.current) {
      setCurrentIndex(calculatedIndex);

      if (!isTranscriptSearchOpen) {
        listRef.current.scrollToItem(calculatedIndex - 1, 'start');
      }

      setUserScrolled(false);
    } else if (calculatedIndex !== currentIndex && listRef.current) {
      setCurrentIndex(calculatedIndex);

      if (!userScrolled && !isTranscriptSearchOpen) {
        listRef.current.scrollToItem(calculatedIndex - 1, 'start');
      }
    }
  }, [
    currentPlayTime,
    currentIndex,
    setCurrentIndex,
    phrases,
    userScrolled,
    firstHookCall,
    emojiClicked,
    paused,
    dispatch,
    isTranscriptSearchOpen,
  ]);

  useEffect(() => {
    if (
      listRef.current &&
      currentIndex !== null &&
      !isTranscriptSearchOpen &&
      !userScrolled
    ) {
      listRef.current.scrollToItem(currentIndex - 1, 'start');
    }
  }, [isTranscriptSearchOpen, currentIndex, userScrolled]);

  useEffect(() => {
    if (currentIndex !== null && currentIndex < firstVisibleIndex + 2) {
      setButtonLocation(TOP);
      setArrowDirection(UP);
    } else {
      setButtonLocation(BOTTOM);
      setArrowDirection(DOWN);
    }
  }, [currentIndex, firstVisibleIndex]);

  useEffect(() => {
    if (listRef.current && isTranscriptSearchOpen && csIndex > -1) {
      listRef.current.scrollToItem(csIndex - 1, 'start');
    }
  }, [searchPhrase, csIndex, counter, isTranscriptSearchOpen]);

  const onRowsRendered = ({ visibleStopIndex, visibleStartIndex }) => {
    setAtEnd(visibleStopIndex === phrases.length - 1);

    if (visibleStartIndex !== firstVisibleIndex) {
      setFirstVisibleIndex(visibleStartIndex);
    }
  };

  const updateUserScrolled = ({ scrollOffset, scrollUpdateWasRequested }) => {
    setShowFullHeader(scrollOffset === 0);

    if (
      !isTranscriptSearchOpen &&
      !scrollUpdateWasRequested &&
      !atEnd &&
      scrollOffset > 0 &&
      !emojiClicked
    ) {
      setUserScrolled(true);

      analytics.track(TRANSCRIPTION_NAVIGATED_BY_USER_SCROLL, {
        currentUserIsOwner,
        video_id: videoId,
      });
    }

    if (emojiClicked) {
      dispatch(updateEmojiClickedInVideoPlayer(false));
    }
  };

  const backToActiveScrolling = () => {
    if (currentIndex !== null) {
      listRef.current?.scrollToItem(currentIndex - 1, 'start');
      setUserScrolled(false);
    }

    analytics.track(TRANSCRIPTION_BACK_TO_ACTIVE_SCROLLING_CLICK, {
      currentUserIsOwner,
      video_id: videoId,
    });
  };

  const getClickedWord = clickedPhrase => {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount < 1) {
      return { word: '' };
    }

    const node = selection.anchorNode;
    const wordNode = (node?.parentNode as HTMLSpanElement).dataset?.isWord
      ? (node?.parentNode as HTMLSpanElement)
      : (node?.parentNode?.parentNode as HTMLSpanElement).dataset?.isWord
      ? (node?.parentNode?.parentNode as HTMLSpanElement)
      : null;

    // since we're wrapping each word in a span, when a word is clicked the
    // textContent of the node will be exactly that one word.
    const word = wordNode?.textContent;

    if (node?.nodeType !== TEXT_NODE || !word?.match(wordRegExp)) {
      return { word: '' };
    }

    const startIndex = parseInt(wordNode?.dataset?.startIndex ?? '-1');
    const occurrenceIndex = findIndexOfOccurrence(
      clickedPhrase.value,
      word,
      startIndex
    );

    const sourceTag = wordNode?.dataset?.sourceTag ?? '-1';

    return {
      word,
      occurrenceIndex,
      sourceTag,
    };
  };

  const handleRowClick = index => {
    const selection = document.getSelection();
    const clickedPhrase = phrases[index];
    const clickedPhraseTs = clickedPhrase['ts'];

    // if user has highlighted the phrase text rather
    // than clicked on a row then the selection type will be range
    // and we want to return rather than update the current player time
    if (selection?.type === 'Range') {
      return;
    }

    setCurrentIndex(index);

    if (panelMode === TRANSCRIPT_PANEL_MODE_CORRECTION) {
      if (!correctionsEnabled) {
        // bail if we're temporarily blocking corrections
        return;
      }

      // detect clicked word
      const { word, occurrenceIndex, sourceTag } =
        getClickedWord(clickedPhrase);

      if (word !== '') {
        // an actual word was clicked
        dispatch(
          openCorrectionPopup({
            textToCorrect: word,
            selectedOccurrenceIndex: occurrenceIndex,
            selectedElementSource: sourceTag,
            editedRowIndex: index,
          })
        );

        analytics.track(TRANSCRIPTION_CORRECTION_WORD_SELECTED, {
          video_id: videoId,
        });
        // TODO: SFP - reuse some logic from the search to highlight all occurrences of this word. The
        //                clicked instance should be the "active"
      }

      // prevent playing the video under correction mode
      return;
    }

    dispatch(requestVideoPlayerCurrentTime(clickedPhraseTs));

    if (!hasStarted) {
      dispatch(updateVideoPlayerPlaying());
      dispatch(requestVideoPlayerPlay());
    }

    analytics.track(TRANSCRIPTION_NAVIGATED_BY_PHRASE_CLICK, {
      currentUserIsOwner,
      video_id: videoId,
    });
  };

  const getSingleCorrection = (newValue, original) => {
    const phrase = phrases[editedRowIndex];
    const sourceIndexes = findTranscriptSourceIndexes([phrase], original);

    const sources = [sourceIndexes[selectedOccurrenceIndex]];

    return { newValue, sources };
  };

  const handleCorrectOne = (newValue, original) => {
    const correction = getSingleCorrection(newValue, original);

    dispatch(applyLocalCorrection(videoId, original, correction));

    analytics.track(TRANSCRIPTION_CORRECTION_CORRECT_SINGLE_WORD, {
      video_id: videoId,
    });
  };

  const handleCorrectAll = (newValue, original) => {
    const sources = findTranscriptSourceIndexes(phrases, original);
    const correction = { newValue, sources };

    dispatch(applyLocalCorrection(videoId, original, correction));

    analytics.track(TRANSCRIPTION_CORRECTION_CORRECT_ALL, {
      video_id: videoId,
      updateLength: sources.length,
    });
  };

  const handleDelete = original => {
    const correction = getSingleCorrection('', original);

    dispatch(applyLocalCorrection(videoId, original, correction));

    analytics.track(TRANSCRIPTION_CORRECTION_DELETE_WORD, {
      video_id: videoId,
    });
  };

  const handleToastClose = () => {
    dispatch(updateTranscriptCorrectionMessage(null));
  };

  if (phrases === undefined) {
    return <EmptyTranscript failed={true} />;
  }

  if (phrases.length < 1) {
    return <EmptyTranscript empty={true} />;
  }

  const rowProps = {
    currentIndex,
    setRowHeight,
    phrases,
    onRowClick: handleRowClick,
    hasStarted,
    csIndex,
    counter,
  };

  return (
    <>
      <Toast
        isOpen={showCorrectionToastMessage}
        onCloseClick={handleToastClose}
      >
        {correctionMessage}
      </Toast>
      <AutoSizer>
        {({ height, width }) => (
          <List
            height={height}
            itemCount={phrases.length}
            itemSize={getRowHeight}
            estimatedItemSize={getRowHeight()}
            width={width}
            ref={listRef}
            onScroll={updateUserScrolled}
            overscanCount={20}
            onItemsRendered={onRowsRendered}
          >
            {({ index, style }) => (
              // @ts-expect-error ignore due to enabling strict null checks
              <TranscriptRow index={index} style={style} {...rowProps} />
            )}
          </List>
        )}
      </AutoSizer>
      {userScrolled &&
        hasStarted &&
        currentIndex !== phrases.length - 1 &&
        !isTranscriptSearchOpen && (
          <div
            className={classNames(
              styles.btnContainer,
              buttonLocation === TOP ? styles.top : styles.bottom
            )}
          >
            <Button
              variant="primary"
              icon={
                arrowDirection === UP ? (
                  <SvgArrowUpward />
                ) : (
                  <SvgArrowDownward />
                )
              }
              hasFullWidth
              onClick={backToActiveScrolling}
            >
              Back to current time
            </Button>
          </div>
        )}
      {popUpIsOpen && (
        <TranscriptCorrectionPopup
          value={textToBeCorrected}
          onCorrectOne={handleCorrectOne}
          onCorrectAll={handleCorrectAll}
          onDeleteWord={handleDelete}
        />
      )}
    </>
  );
};

export default TranscriptList;
